import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { PaginationPageSize } from "@/components/ui/pagination";
import {
  BarChart3,
  TrendingUp,
  IndianRupee,
  Users,
  Calendar,
  Info,
  Dot,
} from "lucide-react";
import { toast } from "sonner";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Line } from "react-chartjs-2";

import {
  getDepartmentWisePayroll,
  getPayrollAnalyticsStats,
  getPayrollDetailedAnalytics,
} from "@/services/adminService";
import { getMonthlyPayrollTrend } from "@/services/adminService";
import { Doughnut } from "react-chartjs-2";
import { formatEnumLabel } from "@/lib/utils";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ArcElement
);

const PayrollAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [trend, setTrend] = useState({ months: [], payroll: [] });
  const [deptDist, setDeptDist] = useState({ departments: [], values: [] });
  const [runs, setRuns] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [pageSize, setPageSize] = useState(6);

  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setPage(1); // reset to first page; triggers refetch via existing effect
  };

  useEffect(() => {
    fetchStats();
    loadTrend();
    loadDepartmentDist();
  }, []);

  useEffect(() => {
    loadRuns(page);
  }, [page, pageSize]);

  const loadTrend = async () => {
    try {
      const data = await getMonthlyPayrollTrend();
      setTrend(data);
    } catch (e) {
      console.error("Trend load error", e);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await getPayrollAnalyticsStats();
      setStats(response);
    } catch (err) {
      toast.error("Failed to load payroll analytics");
    } finally {
      setLoading(false);
    }
  };

  const loadDepartmentDist = async () => {
    try {
      const data = await getDepartmentWisePayroll();
      setDeptDist(data.data);
    } catch (e) {
      console.error("Department distribution load error", e);
    }
  };

  const loadRuns = async (pageNo = 1) => {
    try {
      const res = await getPayrollDetailedAnalytics({
        page: pageNo,
        limit: pageSize,
      });

      setRuns(res.data);
      setPagination(res.pagination);
    } catch (e) {
      console.error("Run history error", e);
    }
  };

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN")}`;

  const percentText = (value) => {
    if (value === 0) return "0%";

    return `${value > 0 ? "+" : ""}${Number(value).toFixed(2)}%`;
  };

  const colorClass = (value) =>
    value > 0 ? "text-green-600" : value < 0 ? "text-red-600" : "text-gray-500";

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-9 w-72" />
          <Skeleton className="h-4 w-96 max-w-full" />
          <Skeleton className="h-7 w-96 max-w-full" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={`payroll-analytics-stat-skeleton-${i}`}>
              <CardHeader className="space-y-2 pb-2">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-4 rounded-full" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-3 w-40" />
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={`payroll-analytics-chart-skeleton-${i}`}>
              <CardHeader>
                <Skeleton className="h-6 w-52" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-64 w-full rounded-xl" />
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-56" />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton
                  key={`payroll-analytics-history-skeleton-${i}`}
                  className="h-48 w-full rounded-xl"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6">
        <p className="text-gray-600">No analytics available.</p>
      </div>
    );
  }

  const typeColors = {
    regular: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    termination:
      "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    one_time:
      "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
  };

  const chartData = {
    labels: trend.months,
    datasets: [
      {
        label: "Payroll (₹)",
        data: trend.payroll,
        borderColor: "rgb(53, 162, 235)",
        backgroundColor: "rgba(53, 162, 235, 0.4)",
        tension: 0.3,
        borderWidth: 2,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => "₹" + Number(value).toLocaleString("en-IN"),
        },
      },
    },
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Payroll Analytics
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Comprehensive payroll insights and trends
          </p>
          <div
            className="mt-2 inline-flex items-center px-2 py-0.5 text-xs rounded-md 
    bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
          >
            <span className="font-medium">
              <Info className="w-4" />
            </span>
            <span className="ml-1">
              Data reflects the most recent completed payroll run
            </span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Payroll */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Payroll</CardTitle>
            <IndianRupee className="h-4 w-4 text-muted-foreground" />
          </CardHeader>

          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.this_month_payroll)}
            </div>
            <p
              className={`text-xs ${colorClass(
                stats.payroll_percentage_change,
              )}`}
            >
              {percentText(stats.payroll_percentage_change)} from last month
            </p>
          </CardContent>
        </Card>

        {/* Active Employees */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Active Employees
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>

          <CardContent>
            <div className="text-2xl font-bold">{stats.active_employees}</div>

            {(() => {
              const diff =
                (stats.active_employees || 0) -
                (stats.previous_active_employees || 0);

              if (diff === 0) {
                return (
                  <p className="text-xs text-gray-500">
                    No additions this month
                  </p>
                );
              }

              const prefix = diff > 0 ? "+" : ""; // negative already has '-'

              return (
                <p
                  className={`text-xs ${
                    diff > 0 ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {prefix}
                  {diff} this month
                </p>
              );
            })()}
          </CardContent>
        </Card>

        {/* Average Salary */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Avg Salary</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>

          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(stats.avg_salary)}
            </div>
            <p className={`text-xs ${colorClass(stats.avg_salary_change)}`}>
              {percentText(stats.avg_salary_change)} from last month
            </p>
          </CardContent>
        </Card>

        {/* Payroll Runs */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Payroll Runs</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>

          <CardContent>
            <div className="text-2xl font-bold">
              {stats.total_payroll_runs_this_year}
            </div>
            <p className="text-xs text-gray-500">This year</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Payroll Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Monthly Payroll Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {trend?.months?.length > 0 ? (
                <Line data={chartData} options={chartOptions} />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  No payroll history available.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Department Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Department-wise Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {deptDist?.departments?.length > 0 ? (
                <Doughnut
                  key={deptDist.departments.join("-")} // <-- FIX
                  data={{
                    labels: deptDist.departments,
                    datasets: [
                      {
                        data: deptDist.values,
                        backgroundColor: [
                          "rgba(59, 130, 246, 0.7)", // Blue
                          "rgba(16, 185, 129, 0.7)", // Green
                          "rgba(249, 115, 22, 0.7)", // Orange
                          "rgba(236, 72, 153, 0.7)", // Pink
                          "rgba(139, 92, 246, 0.7)", // Purple
                          "rgba(234, 179, 8, 0.7)", // Yellow
                          "rgba(20, 184, 166, 0.7)", // Teal
                        ].slice(0, deptDist.departments.length),

                        borderColor: "rgba(255,255,255,0.8)",
                        borderWidth: 2,
                        hoverOffset: 12,
                      },
                    ],
                  }}
                  options={{
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: {
                          usePointStyle: true,
                          pointStyle: "circle",
                          padding: 18,
                          font: { size: 12 },
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: (ctx) => {
                            const label = ctx.label || "";
                            const value = ctx.raw || 0;
                            return `${label}: ₹${Number(value).toLocaleString(
                              "en-IN",
                            )}`;
                          },
                        },
                      },
                    },
                    cutout: "65%", // for professional inner spacing
                    responsive: true,
                    maintainAspectRatio: false,
                  }}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  No department data available.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed History */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Payroll History</CardTitle>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No previous payroll runs found.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {runs.map((run) => {
                  const monthName = new Date(
                    run.period_year,
                    run.period_month - 1,
                  ).toLocaleString("default", { month: "long" });

                  // Status styling
                  const statusColors = {
                    completed:
                      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300",
                    locked:
                      "bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300",
                    draft:
                      "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
                  };

                  return (
                    <div
                      key={run._id}
                      className="p-5 rounded-xl border border-gray-200 dark:border-gray-700 
                         bg-white dark:bg-gray-900 shadow-md hover:shadow-lg transition-all"
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="text-sm flex items-center text-gray-500 dark:text-gray-400">
                            {monthName} {run.period_year}
                            <Dot size={20} />
                            {formatEnumLabel(run.type || "regular")}
                          </div>

                          <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                            {run.employees} Employees
                          </p>
                        </div>

                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold capitalize 
      ${statusColors[run.status]}`}
                        >
                          {run.status}
                        </span>
                      </div>

                      {/* Body */}
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">
                            Total Gross
                          </span>
                          <span className="font-semibold">
                            {formatCurrency(run.total_gross)}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">
                            Deductions
                          </span>
                          <span className="font-semibold">
                            {formatCurrency(run.total_deductions)}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">
                            Net Payable
                          </span>
                          <span className="font-bold text-primary">
                            {formatCurrency(run.total_net)}
                          </span>
                        </div>

                        <div className="flex justify-between mt-3 pt-2 border-t border-gray-300 dark:border-gray-700">
                          <span className="text-gray-600 dark:text-gray-400">
                            Processed
                          </span>
                          <span className="font-medium">
                            {new Date(run.run_date).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-center sm:justify-between gap-4 mt-6">
                  <PaginationPageSize
                    value={pageSize}
                    onValueChange={handlePageSizeChange}
                    options={[6, 12, 24, 48]}
                  />
                  <div className="flex items-center gap-4">
                    <button
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                      className="px-3 py-1 border rounded disabled:opacity-40"
                    >
                      Prev
                    </button>

                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>

                    <button
                      disabled={page === pagination.totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(p + 1, pagination.totalPages))
                      }
                      className="px-3 py-1 border rounded disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PayrollAnalytics;
